import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { code128Svg, sanitizeForCode128 } from '@/lib/code128'
import { formatSheetDateTime, progressLabel, timeLeftLabel } from '@/lib/pickList'
import { PERMISSIONS as P, usePermission } from '@/lib/permissions'
import { usePickListAssignableUsers, useUpdatePickList } from '@/hooks/usePickLists'
import type { PickList } from '@/types/pickList'

/**
 * The sheet header, laid out as in the spec: identity + barcode + status chips
 * on the left, pick progress on the right, then a single-row metadata strip
 * carrying the same eight fields the printed sheet does.
 */
export function PickListHeader({ pickList }: { pickList: PickList }) {
  const barcode = code128Svg(sanitizeForCode128(pickList.code), {
    moduleWidth: 1.15,
    height: 26,
    title: `${pickList.code} rev ${pickList.revision}`,
  })
  const p = pickList.summary.progress
  const pct = p.total > 0 ? Math.round((p.picked / p.total) * 100) : 0
  const due = timeLeftLabel(pickList.requiredBy)

  return (
    <div className='rounded-xl border bg-card p-5'>
      <div className='flex flex-wrap items-start justify-between gap-6'>
        {/* Identity */}
        <div className='min-w-0'>
          <div className='flex items-baseline gap-2'>
            <h1 className='text-2xl font-bold tracking-tight'>{pickList.code}</h1>
            <span className='text-sm text-muted-foreground'>rev {pickList.revision}</span>
          </div>
          <p className='mt-1 text-xs text-muted-foreground'>
            Reagents, consumables and kits · picked in zone route order · FEFO lots suggested by
            the system
          </p>
          {barcode && (
            <div
              className='mt-2.5'
              aria-hidden
              dangerouslySetInnerHTML={{ __html: barcode }}
            />
          )}

          <div className='mt-3 flex flex-wrap items-center gap-1.5'>
            <Chip tone={pickList.priority === 'HIGH' ? 'red' : 'slate'} dot>
              {priorityLabel(pickList.priority)} priority
            </Chip>
            <Chip tone='slate'>{statusLabel(pickList.status)}</Chip>
            <Chip tone='slate'>{pickList.allocationRule} allocation</Chip>
            {due && <Chip tone={due.overdue ? 'red' : 'amber'}>{due.text}</Chip>}
          </div>
        </div>

        {/* Progress */}
        <div className='w-full max-w-sm'>
          <div className='flex items-baseline justify-between'>
            <span className='text-base font-bold'>{progressLabel(p.picked, p.total)}</span>
            <span className='text-xs text-muted-foreground tabular-nums'>{pct}%</span>
          </div>
          <div className='mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-200'>
            <div
              className='h-full rounded-full bg-amber-500 transition-all'
              style={{ width: `${Math.max(pct, p.total > 0 ? 2 : 0)}%` }}
            />
          </div>
          <div className='mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground'>
            <Legend cls='bg-emerald-500' label='Picked' n={p.picked} />
            <Legend cls='bg-amber-500' label='Flagged' n={p.flagged} />
            <Legend cls='bg-red-500' label='Short' n={p.short} />
            <Legend cls='bg-slate-300' label='Open' n={p.open} />
          </div>
        </div>
      </div>

      {/* Metadata strip — one row of eight, matching the printed sheet. */}
      <dl className='mt-4 grid grid-cols-2 gap-x-6 gap-y-3 rounded-lg border bg-muted/20 px-4 py-3 sm:grid-cols-4 xl:grid-cols-8'>
        <Meta k='REQUESTED BY' v={pickList.requestedBy} />
        <AssigneeMeta pickList={pickList} />
        <Meta k='DESTINATION' v={pickList.destination} />
        <Meta k='ORDER REFERENCE' v={pickList.orderReference} />
        <Meta k='MANUFACTURING ORDER' v={pickList.manufacturingOrder} />
        <Meta k='CHAIN OF IDENTITY' v={pickList.chainOfIdentity} />
        <Meta k='CREATED' v={formatSheetDateTime(pickList.generatedAt)} />
        <Meta k='REQUIRED BY' v={formatSheetDateTime(pickList.requiredBy)} />
      </dl>
    </div>
  )
}

/**
 * Who owns this sheet, and the one control that changes it.
 *
 * Assignment is what puts a sheet on somebody's phone: the mobile worklist
 * matches pick lists on `assignedToId`, so an unassigned sheet reaches nobody.
 * It sits in the metadata strip rather than behind a dialog because it is the
 * same fact the printed sheet carries, and reading it is the common case.
 *
 * Gated on `pick_lists.generate` — the permission `PATCH :id` itself requires.
 * Without it this is the plain text it always was, not a control that 403s.
 */
function AssigneeMeta({ pickList }: { pickList: PickList }) {
  const { can } = usePermission()
  const canAssign = can(P.pickListsGenerate)
  const { data: users = [] } = usePickListAssignableUsers(canAssign)
  // Hooks run unconditionally, so the id is only checked below. A null one is a
  // PREVIEW: `POST /preview` resolves a sheet without saving it, and there is no
  // record to assign until someone generates it.
  const update = useUpdatePickList(pickList.id ?? '')

  if (!canAssign || !pickList.id) {
    return <Meta k='ASSIGNED TO' v={pickList.assignedToName ?? 'Unassigned'} />
  }

  // The roster holds enabled members only, so an assignee disabled or removed
  // from the tenant after the fact is missing from it. Their name is still on
  // the sheet, so it is carried as an option rather than silently reading as
  // Unassigned, which would look like the assignment was lost.
  const known = users.some((u) => u.id === pickList.assignedToId)
  const options = [
    ...users,
    ...(pickList.assignedToId && !known
      ? [{ id: pickList.assignedToId, name: pickList.assignedToName ?? 'Former member', email: '' }]
      : []),
  ]

  return (
    <div className='min-w-0'>
      <dt className='flex items-center gap-1 text-[9px] font-semibold uppercase leading-tight tracking-wider text-muted-foreground'>
        ASSIGNED TO
        {update.isPending && <Loader2 className='h-2.5 w-2.5 animate-spin' aria-hidden />}
      </dt>
      <dd className='mt-0.5'>
        <select
          aria-label='Assign this pick list to'
          className='h-6 w-full max-w-full rounded border border-input bg-background px-1 text-xs font-semibold disabled:opacity-60'
          disabled={update.isPending}
          value={pickList.assignedToId ?? ''}
          onChange={(e) => update.mutate({ assignedToId: e.target.value || null })}
        >
          <option value=''>Unassigned</option>
          {options.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name}
            </option>
          ))}
        </select>
        {update.error && (
          <p className='mt-1 text-[10px] font-medium text-red-600'>
            {update.error instanceof Error ? update.error.message : 'Could not assign this sheet.'}
          </p>
        )}
      </dd>
    </div>
  )
}

function Legend({ cls, label, n }: { cls: string; label: string; n: number }) {
  return (
    <span className='inline-flex items-center gap-1.5'>
      <span className={cn('h-2 w-2 rounded-[2px]', cls)} />
      {label} <span className='font-semibold text-foreground tabular-nums'>{n}</span>
    </span>
  )
}

const CHIP_TONES = {
  red: 'border-red-200 bg-red-50 text-red-700',
  amber: 'border-amber-200 bg-amber-50 text-amber-800',
  slate: 'border-slate-200 bg-slate-50 text-slate-600',
} as const

function Chip({
  tone,
  dot,
  children,
}: {
  tone: keyof typeof CHIP_TONES
  dot?: boolean
  children: React.ReactNode
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-medium',
        CHIP_TONES[tone],
      )}
    >
      {dot && <span className='h-1.5 w-1.5 rounded-full bg-current' />}
      {children}
    </span>
  )
}

function Meta({ k, v }: { k: string; v: string | null | undefined }) {
  return (
    <div className='min-w-0'>
      <dt className='text-[9px] font-semibold uppercase leading-tight tracking-wider text-muted-foreground'>
        {k}
      </dt>
      {/* Wraps rather than truncates: an assignee or order reference cut mid-word
          is worse than a two-line cell, and the mockup shows them wrapped. */}
      <dd className='mt-0.5 text-xs font-semibold leading-snug' title={v ?? undefined}>
        {v || '—'}
      </dd>
    </div>
  )
}

function priorityLabel(p: PickList['priority']): string {
  return { LOW: 'Low', NORMAL: 'Normal', HIGH: 'High' }[p]
}

function statusLabel(s: PickList['status']): string {
  return {
    DRAFT: 'Draft',
    OPEN: 'Not started',
    IN_PROGRESS: 'In progress',
    COMPLETED: 'Completed',
    CANCELLED: 'Cancelled',
  }[s]
}
